/**
 * FEAT-01 Kanban board on the Tasks page. Columns come from the space's own statuses when one space is chosen;
 * across all spaces the columns are the five canonical states (Open, In progress, Waiting, Done, Cancelled) and a
 * drop maps to the item's own space's first status of that kind. Moving a card calls the same
 * POST /api/work/items/:id/status as the task page, so permissions, completion blockers and sign-off rules apply.
 * Drag on desktop, a status select on the card for touch.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { Star, Users } from "lucide-react";
import { useState } from "react";
import { post } from "../lib/api";
import { isMustDo, type Item, type Space, type StatusDef } from "../lib/work";
import { Due, PriorityBadge } from "./work";

type Col = { key: string; label: string; canonical: StatusDef["canonical"] };

const CANON: Array<[StatusDef["canonical"], string]> = [["open", "Open"], ["in_progress", "In progress"], ["waiting", "Waiting"], ["done", "Done"], ["cancelled", "Cancelled"]];
const DOT: Record<string, string> = { open: "bg-slate-400", in_progress: "bg-brand-500", waiting: "bg-amber-500", done: "bg-emerald-500", cancelled: "bg-zinc-400" };

function columnsFor(space: Space | undefined): Col[] {
  if (space) return space.statuses.map((s) => ({ key: s.key, label: s.label, canonical: s.canonical }));
  return CANON.map(([c, label]) => ({ key: c, label, canonical: c }));
}

/** The status key an item gets when dropped on a column: the column's own status, or (all spaces) its space's first status of that kind. */
function targetStatus(i: Item, col: Col, space: Space | undefined, spaces: Space[]): string | null {
  if (space) return col.key;
  const own = spaces.find((s) => s.id === i.spaceId);
  return own?.statuses.find((s) => s.canonical === col.canonical)?.key ?? null;
}

function colOf(i: Item, space: Space | undefined): string {
  return space ? i.statusKey : i.state;
}

export function Board({ items, space, spaces }: { items: Item[]; space: Space | undefined; spaces: Space[] }) {
  const qc = useQueryClient();
  const cols = columnsFor(space);
  const [drag, setDrag] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // optimistic column while the status call runs, so the card doesn't jump back and forth
  const [moved, setMoved] = useState<Record<string, string>>({});

  const move = async (i: Item, col: Col) => {
    const statusKey = targetStatus(i, col, space, spaces);
    if (!statusKey) { alert(`${i.spaceName ?? "This space"} has no "${col.label}" status`); return; }
    if (statusKey === i.statusKey && !i.pendingVerification) return;
    setBusy(i.id);
    setMoved((m) => ({ ...m, [i.id]: col.key }));
    try {
      await post(`/api/work/items/${i.id}/status`, { statusKey });
      for (const k of ["items", "item", "my-day", "plan-today", "week"]) void qc.invalidateQueries({ queryKey: [k] });
    } catch (err) {
      setMoved((m) => { const { [i.id]: _, ...rest } = m; return rest; });
      const e = err as { message?: string; details?: { blockers?: Array<{ reason?: string; title?: string }> } };
      const why = e.details?.blockers?.map((b) => b.reason ?? b.title).filter(Boolean).join("; ");
      alert(why ? `Can't move it yet: ${why}` : e.message ?? "Couldn't move the task");
    } finally {
      setBusy(null);
    }
  };

  const byCol = new Map<string, Item[]>();
  for (const i of items) {
    const k = moved[i.id] ?? colOf(i, space);
    byCol.set(k, [...(byCol.get(k) ?? []), i]);
  }
  // closed columns: newest finished first, capped so the board stays light
  const closedSort = (a: Item, b: Item) => (b.completedAt ?? b.createdAt).localeCompare(a.completedAt ?? a.createdAt);

  return (
    <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-3" role="list" aria-label="Board">
      {cols.map((col) => {
        const closed = col.canonical === "done" || col.canonical === "cancelled";
        const list = closed ? [...(byCol.get(col.key) ?? [])].sort(closedSort).slice(0, 30) : byCol.get(col.key) ?? [];
        const total = byCol.get(col.key)?.length ?? 0;
        return (
          <section key={col.key} role="listitem" aria-label={col.label}
            className={clsx("flex w-[17rem] shrink-0 flex-col rounded-2xl border bg-[#f4f5fb] transition-colors", over === col.key && drag ? "border-brand-400 bg-brand-50" : "border-[var(--line)]")}
            onDragOver={(e) => { if (drag) { e.preventDefault(); setOver(col.key); } }}
            onDragLeave={() => setOver((o) => (o === col.key ? null : o))}
            onDrop={(e) => { e.preventDefault(); const i = items.find((x) => x.id === drag); setDrag(null); setOver(null); if (i) void move(i, col); }}>
            <header className="flex items-center gap-2 px-3 pt-3 pb-2">
              <span className={clsx("h-2 w-2 rounded-full", DOT[col.canonical])} aria-hidden />
              <h3 className="text-sm font-semibold">{col.label}</h3>
              <span className="count ml-auto rounded-full bg-[rgb(11_12_42/0.07)] px-2 text-[0.6875rem] font-bold">{total}</span>
            </header>
            <ul className="flex min-h-[6rem] flex-1 flex-col gap-2 px-2 pb-2">
              {list.length === 0 && <li className="muted px-2 py-4 text-center text-xs">{drag ? "Drop here" : "Nothing here"}</li>}
              {list.map((i) => (
                <Card key={i.id} i={i} cols={cols} col={moved[i.id] ?? colOf(i, space)} busy={busy === i.id} dragging={drag === i.id} showSpace={!space}
                  onDragStart={() => setDrag(i.id)} onDragEnd={() => { setDrag(null); setOver(null); }} onMove={(k) => { const c = cols.find((x) => x.key === k); if (c) void move(i, c); }} />
              ))}
              {closed && total > list.length && <li className="muted px-2 py-1 text-center text-xs">{total - list.length} older in the list view</li>}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function Card({ i, cols, col, busy, dragging, showSpace, onDragStart, onDragEnd, onMove }: { i: Item; cols: Col[]; col: string; busy: boolean; dragging: boolean; showSpace: boolean; onDragStart: () => void; onDragEnd: () => void; onMove: (key: string) => void }) {
  const closed = i.state === "done" || i.state === "cancelled";
  return (
    <li draggable={!busy} onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart(); }} onDragEnd={onDragEnd}
      className={clsx("rounded-xl border border-[var(--line)] bg-[var(--panel)] shadow-sm transition-opacity", dragging && "opacity-40", busy && "animate-pulse")}>
      <Link to="/work/$id" params={{ id: i.id }} className="block cursor-grab px-3 pt-2.5 pb-2 active:cursor-grabbing">
        <span className="flex items-start gap-1.5">
          {isMustDo(i) && <Star size={13} className="mt-0.5 shrink-0 fill-amber-400 text-amber-400" aria-label="Must-do" />}
          <span className={clsx("text-sm font-semibold leading-snug", closed ? "text-[var(--muted)] line-through" : "text-[var(--text)]")}>{i.title}</span>
        </span>
        <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--muted)]">
          <span className="num font-medium">{i.ref}</span>
          {showSpace && i.spaceName && <span>· {i.spaceName}</span>}
          <PriorityBadge p={i.priority} />
          <Due at={i.dueAt} />
          {i.pendingVerification && <span className="rounded-full bg-violet-50 px-1.5 font-semibold text-violet-700">Awaiting sign-off</span>}
          {i.subtasks && <span>{i.subtasks.done}/{i.subtasks.total} sub-tasks</span>}
          {i.assigneeNames && i.assigneeNames.length > 0 && <span className="inline-flex items-center gap-1"><Users size={11} /> {i.assigneeNames.join(", ")}</span>}
        </span>
      </Link>
      <div className="border-t border-[var(--line)] px-2 py-1 md:hidden">
        <select className="w-full bg-transparent text-xs" value={col} aria-label={`Move ${i.title}`} disabled={busy} onChange={(e) => onMove(e.target.value)}>
          {cols.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
    </li>
  );
}
